// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the chat's ClickUp tasks in Time and client (design/agents-task-linking A, B, C; phone
// p-t-p). One task: its row, and when Konvo linked it by itself why, with "Not this one". None yet:
// "Konvo suggests" up to three open tasks with a reason each (the best outlined), one tap to link,
// "Find another task" (the task picker) and "None of these". Several: each task with its turns,
// agent time and the person's minutes, a strip with a block per turn in the task's colour, "Check
// the turns" (AgentTimeTurns) and "Add a task". Linking never writes to ClickUp.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {RunningRing} from '@app/features/konvo_agents/components/AgentBits';
import {agentErrorMessage, coarseDuration} from '@app/features/konvo_agents/components/AgentsCopy';
import styles from '@app/features/konvo_agents/components/AgentTimePanel.module.css';
import {minutesAmount} from '@app/features/konvo_agents/components/AgentTimeYou';
import {agentErrorCode} from '@app/features/konvo_agents/state/AgentsModel';
import AgentTime from '@app/features/konvo_agents/state/AgentTime';
import {StatusRing} from '@app/features/konvo_tasks/components/workspace/shared/StatusRing';
import type {KonvoTask} from '@app/features/konvo_tasks/state/KonvoTasks';
import {clickUpTaskIdFromUrl} from '@app/features/konvo_tasks/utils/ClickUpTaskLink';
import {http} from '@app/features/platform/transport/RestTransport';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import type {
	AgentTaskLinkWhy,
	AgentTaskSuggestion,
	AgentTimeTask,
	AgentTimeTaskShare,
	AgentTimeView,
} from '@fluxer/schema/src/domains/konvo/AgentTime';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconLinkOff, IconPlus, IconSearch, IconSparkles} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

/** One calm hue per task, in the order they were linked (the strip, the Turns view, the log). */
export const TASK_COLORS = ['#4a68d6', '#2f9e8f', '#c98a12', '#b5487a', '#6b5bd2', '#3f8f6a', '#c0603a', '#5b7a99'];
export const taskColor = (index: number) => TASK_COLORS[index % TASK_COLORS.length]!;

const fold = (value: string) => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
export const capitalise = (value: string) => (value ? value[0]!.toLocaleUpperCase() + value.slice(1) : value);

const TURN_ONE = msg({
	message: 'Turn {n}',
	comment: 'Agents Time and client: the one turn that counts for a task, e.g. "Turn 3".',
});
const TURN_TWO = msg({
	message: 'Turns {a} and {b}',
	comment: 'Agents Time and client: the two turns that count for a task, e.g. "Turns 5 and 6".',
});
const TURN_RANGE = msg({
	message: 'Turns {from} to {to}',
	comment: 'Agents Time and client: the run of turns that count for a task, e.g. "Turns 1 to 4".',
});
const TURN_COUNT = msg({
	message: '{count, plural, one {# turn} other {# turns}}',
	comment: 'Agents Time and client: how many turns count for a task when they are not one run, e.g. "5 turns".',
});
const TURN_NONE = msg({message: 'No turns yet', comment: 'Agents Time and client: no turn counts for this task yet.'});

/** "Turn 3", "Turns 5 and 6", "Turns 1 to 4", "5 turns". */
export function turnsLabel(i18n: I18n, turns: ReadonlyArray<number>): string {
	const sorted = [...new Set(turns.filter((n) => n > 0))].sort((a, b) => a - b);
	if (sorted.length === 0) return i18n._(TURN_NONE);
	if (sorted.length === 1) return i18n._(TURN_ONE, {n: sorted[0]});
	if (sorted.length === 2) return i18n._(TURN_TWO, {a: sorted[0], b: sorted[1]});
	if (sorted.at(-1)! - sorted[0]! === sorted.length - 1)
		return i18n._(TURN_RANGE, {from: sorted[0], to: sorted.at(-1)});
	return i18n._(TURN_COUNT, {count: sorted.length});
}

const ZERO_MINUTES = msg({message: '{n}m', comment: 'Agents list: minutes since a thread was updated, e.g. "4m".'});

/** "2h 14m", "12m", "0m". */
export function timeAmount(i18n: I18n, ms: number): string {
	if (ms < 30_000) return i18n._(ZERO_MINUTES, {n: 0});
	return coarseDuration(i18n, Math.round(ms / 1000));
}

/** "In review · Konvo · Mail" (phones leave the space out). */
export function taskMeta(task: AgentTimeTask, phone: boolean): string {
	return [task.status ? capitalise(task.status.name) : null, phone ? null : task.space, task.list]
		.filter(Boolean)
		.join(' · ');
}

async function openTask(task: AgentTimeTask): Promise<void> {
	const {openKonvoTask} = await import('@app/features/konvo_tasks/commands/KonvoTaskCommands');
	openKonvoTask(task.id, 'overview', task.url ?? undefined);
}

/** Closes a picker on Escape or a press outside it. */
export function useDismiss(
	ref: React.RefObject<HTMLElement | null>,
	onClose: () => void,
	host: 'parent' | 'self' = 'parent',
) {
	useEffect(() => {
		const away = (event: PointerEvent) => {
			const target = event.target as Node | null;
			const box = host === 'parent' ? ref.current?.parentElement : ref.current;
			if (target && box && !box.contains(target)) onClose();
		};
		const key = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				onClose();
			}
		};
		document.addEventListener('pointerdown', away, true);
		document.addEventListener('keydown', key, true);
		return () => {
			document.removeEventListener('pointerdown', away, true);
			document.removeEventListener('keydown', key, true);
		};
	}, [ref, onClose, host]);
}

interface MyTasks {
	connected: boolean;
	tasks: Array<KonvoTask>;
}

/** The person's open ClickUp tasks to pick from, or a pasted ClickUp link. */
export function TaskPicker({
	phone,
	onPick,
	onClose,
	inline = false,
	className,
}: {
	phone: boolean;
	onPick: (taskId: string) => void;
	onClose: () => void;
	/** In place (a menu or the phone sheet) rather than dropped over the cards. */
	inline?: boolean;
	className?: string;
}) {
	const {t} = useLingui();
	const ref = useRef<HTMLDivElement>(null);
	const [query, setQuery] = useState('');
	const [mine, setMine] = useState<MyTasks | null>(null);
	useDismiss(ref, onClose);
	useEffect(() => {
		let live = true;
		void http
			.get<MyTasks>(Endpoints.KONVO_CLICKUP_MY_TASKS, {mode: 'silent'})
			.then((response) => {
				if (live) setMine(response.ok ? response.body : {connected: false, tasks: []});
			})
			.catch(() => live && setMine({connected: false, tasks: []}));
		return () => {
			live = false;
		};
	}, []);
	const fromLink = clickUpTaskIdFromUrl(query.trim());
	const wanted = fold(query);
	const tasks = (mine?.tasks ?? [])
		.filter((task) => task.id)
		.filter(
			(task) => !wanted || fold(task.name).includes(wanted) || (task.custom_id ?? '').toLowerCase().includes(wanted),
		)
		.slice(0, 30);
	return (
		<div
			ref={ref}
			className={clsx(styles.picker, styles.pickerTask, (phone || inline) && styles.pickerInline, className)}
			data-flx="konvo-agents.time.task-picker"
		>
			<label className={styles.search}>
				<IconSearch size={15} stroke={1.8} />
				<input
					// biome-ignore lint/a11y/noAutofocus: the picker opens to be typed in
					autoFocus={!phone}
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					onKeyDown={(event) => {
						if (event.key !== 'Enter') return;
						event.preventDefault();
						const first = fromLink ?? tasks[0]?.id;
						if (first) onPick(first);
					}}
					placeholder={t({
						message: 'Find your task or paste its link',
						comment: 'Agents task picker: placeholder of the search box (your ClickUp tasks, or a ClickUp link).',
					})}
					aria-label={t({
						message: 'Find your task or paste its link',
						comment: 'Agents task picker: placeholder of the search box (your ClickUp tasks, or a ClickUp link).',
					})}
					data-flx="konvo-agents.time.task-search"
				/>
			</label>
			<div className={styles.options} role="listbox">
				{fromLink && (
					<button
						type="button"
						role="option"
						aria-selected={false}
						className={clsx(styles.option, styles.optionActive)}
						onClick={() => onPick(fromLink)}
						data-flx="konvo-agents.time.task-option.link"
					>
						<ClickUpLogo size={15} />
						<b className={styles.optionName}>
							{t({message: 'Use the linked task', comment: 'Agents task picker: link the task from the pasted URL.'})}
						</b>
						<span className={styles.optionMeta}>{fromLink}</span>
					</button>
				)}
				{mine === null && (
					<span className={styles.optionsNote}>
						<RunningRing size={13} />
					</span>
				)}
				{mine && !mine.connected && !fromLink && (
					<span className={styles.optionsNote}>
						{t({
							message: 'Connect ClickUp to link one of your tasks.',
							comment: 'Agents task picker: ClickUp is not connected, so no tasks can be listed.',
						})}
					</span>
				)}
				{tasks.map((task) => (
					<button
						key={task.id}
						type="button"
						role="option"
						aria-selected={false}
						className={styles.option}
						onClick={() => onPick(task.id!)}
						data-flx="konvo-agents.time.task-option"
					>
						<ClickUpLogo size={15} />
						<b className={styles.optionName}>{task.name}</b>
						{task.list?.name && <span className={styles.optionMeta}>{task.list.name}</span>}
					</button>
				))}
			</div>
		</div>
	);
}

const OPEN_IN_CLICKUP = msg({
	message: 'Open in ClickUp',
	comment: 'Agents Time and client: the ClickUp logo after the linked task’s name, opens the task in ClickUp.',
});
const OPEN_IN_CLICKUP_LABEL = (i18n: I18n) => i18n._(OPEN_IN_CLICKUP);

/** ClickUp's status ring for the task, its status, space and list on hover (like a row in ClickUp's list). */
function TaskStatusRing({task}: {task: AgentTimeTask}) {
	const status = task.status!;
	return (
		<span className={styles.statusRing} title={taskMeta(task, false)} data-flx="konvo-agents.time.task-status">
			<StatusRing
				status={{status: status.name, color: status.color, type: status.type ?? null}}
				progress={status.fill}
				size={16}
			/>
			<span className={styles.srOnly}>{capitalise(status.name)}</span>
		</span>
	);
}

/** Words for a failed change of tasks. */
export function taskProblem(i18n: I18n, problem: unknown): string {
	switch (agentErrorCode(problem)) {
		case 'NOT_FOUND':
			return i18n._(
				msg({
					message: 'ClickUp has no task with that link.',
					comment: 'Agents Time and client: the pasted ClickUp task could not be found.',
				}),
			);
		case 'TASK_LOGGED':
			return i18n._(
				msg({
					message: 'Time was logged to that task from here, so it stays.',
					comment: 'Agents Time and client: a task or turn with time logged to ClickUp cannot be unlinked or moved.',
				}),
			);
		case 'CLICKUP_NOT_CONNECTED':
			return i18n._(msg({message: 'Connect ClickUp first.', comment: 'Agents Log time: ClickUp is not connected.'}));
		default:
			return agentErrorMessage(i18n, problem);
	}
}

// ── Why Konvo linked it ───────────────────────────────────────────────

/** "Konvo linked it: your first prompt has the task's link, and the branch is feat/mail-swipe. Not this one" */
function LinkedWhy({why, onNotThis, phone}: {why: AgentTaskLinkWhy; onNotThis: () => void; phone: boolean}) {
	const {i18n, t} = useLingui();
	const parts: Array<React.ReactNode> = [];
	if (why.model) parts.push(why.model.replace(/\.\s*$/, ''));
	if (why.prompt === 1)
		parts.push(
			t({
				message: 'your first prompt has the task’s link',
				comment: 'Agents Time and client: why Konvo linked the task by itself (the first prompt names it).',
			}),
		);
	else if (why.prompt)
		parts.push(
			i18n._(
				msg({
					message: 'your prompt in turn {n} has the task’s link',
					comment:
						'Agents Time and client: why Konvo linked the task by itself (a later prompt names it), e.g. "turn 5".',
				}),
				{n: why.prompt},
			),
		);
	const branch = why.branch;
	if (branch)
		parts.push(
			<Trans comment="Agents Time and client: why Konvo linked the task by itself; the chat's git branch, e.g. feat/mail-swipe.">
				the branch is <code className={styles.code}>{branch}</code>
			</Trans>,
		);
	if (why.pr && !why.model)
		parts.push(
			i18n._(
				msg({
					message: 'pull request #{number} names it',
					comment: 'Agents Time and client: why Konvo linked the task by itself; the chat’s pull request, e.g. "#300".',
				}),
				{number: why.pr},
			),
		);
	const list = joinNodes(i18n.locale, parts);
	return (
		<div className={clsx(styles.by, phone && styles.byPhone)} data-flx="konvo-agents.time.linked-why">
			<IconSparkles size={15} stroke={1.7} className={styles.byIcon} />
			<span>
				{t({
					message: 'Konvo linked it:',
					comment: 'Agents Time and client: start of why Konvo linked the chat’s ClickUp task by itself.',
				})}{' '}
				{list}.{' '}
				<button type="button" className={styles.linkButton} onClick={onNotThis} data-flx="konvo-agents.time.not-this">
					{t({
						message: 'Not this one',
						comment: 'Agents Time and client: unlink the task Konvo linked by itself and show the options instead.',
					})}
				</button>
			</span>
		</div>
	);
}

/** Nodes joined as a list in the person's language ("a, b and c"). */
function joinNodes(locale: string, nodes: ReadonlyArray<React.ReactNode>): React.ReactNode {
	if (nodes.length <= 1) return nodes[0] ?? null;
	const marks = nodes.map((_, i) => `[[node${i}]]`);
	let parts: Array<{type: string; value: string}>;
	try {
		parts = new Intl.ListFormat(locale, {type: 'conjunction'}).formatToParts(marks);
	} catch {
		parts = marks.flatMap((mark, i) =>
			i === 0
				? [{type: 'element', value: mark}]
				: [
						{type: 'literal', value: ', '},
						{type: 'element', value: mark},
					],
		);
	}
	return parts.map((part, i) => {
		const index = part.type === 'element' ? Number(/\[\[node(\d+)\]\]/.exec(part.value)?.[1] ?? -1) : -1;
		return <span key={i}>{index >= 0 ? nodes[index] : part.value}</span>;
	});
}

// ── The chat's task rows ──────────────────────────────────────────────

interface TaskRowsProps {
	machineId: string;
	threadId: string;
	view: AgentTimeView;
	phone: boolean;
	onTurns: () => void;
	onError: (message: string | null) => void;
}

/** The ClickUp task row(s) of the client card: none (with Konvo's options), one, or several. */
export const TaskRows = observer(({machineId, threadId, view, phone, onTurns, onError}: TaskRowsProps) => {
	const {i18n, t} = useLingui();
	const [picking, setPicking] = useState(false);
	const [busy, setBusy] = useState<string | null>(null);
	const run = async (key: string, change: () => Promise<unknown>) => {
		setBusy(key);
		onError(null);
		try {
			await change();
		} catch (problem) {
			onError(taskProblem(i18n, problem));
		} finally {
			setBusy(null);
		}
	};
	const link = (taskId: string) => {
		setPicking(false);
		void run(`link:${taskId}`, () => AgentTime.changeTasks(machineId, threadId, {op: 'link', taskId}));
	};
	const tasks = view.tasks.length ? view.tasks : view.task ? [legacyShare(view)] : [];
	const label = phone
		? t({message: 'Task', comment: 'Agents Time and client (phone): row label for the chat’s ClickUp task.'})
		: t({message: 'ClickUp task', comment: 'Agents Time and client: row label for the chat’s ClickUp task.'});
	const picker = picking && <TaskPicker phone={phone} onClose={() => setPicking(false)} onPick={link} />;

	if (tasks.length === 0) {
		const suggest = view.suggest;
		const looking = suggest?.status === 'looking';
		const options = suggest && !suggest.dismissed ? suggest.options : [];
		return (
			<>
				<div className={styles.row}>
					<span className={styles.key}>{label}</span>
					{options.length || looking ? (
						<span className={styles.placeholder} data-flx="konvo-agents.time.task-none">
							{t({
								message: 'Not linked yet',
								comment: 'Agents Time and client: the chat has no ClickUp task yet; Konvo suggests some below.',
							})}
						</span>
					) : (
						<button
							type="button"
							className={styles.value}
							onClick={() => setPicking(!picking)}
							aria-expanded={picking}
							data-flx="konvo-agents.time.task-link"
						>
							<IconPlus size={16} stroke={1.7} className={styles.valueIcon} />
							<span className={styles.placeholder}>
								{t({message: 'Link a task', comment: 'Agents Time and client: link a ClickUp task to this chat.'})}
							</span>
						</button>
					)}
				</div>
				{looking && options.length === 0 && (
					<div className={styles.looking} data-flx="konvo-agents.time.looking">
						<RunningRing size={13} />
						{t({
							message: 'Konvo is looking for the task…',
							comment: 'Agents Time and client: Konvo is reading the chat to find its ClickUp task.',
						})}
					</div>
				)}
				{options.length > 0 && (
					<Suggestions
						options={options}
						phone={phone}
						busy={busy}
						onLink={link}
						onFind={() => setPicking(!picking)}
						onNone={() => void run('none', () => AgentTime.changeTasks(machineId, threadId, {op: 'dismiss'}))}
					/>
				)}
				{picker}
			</>
		);
	}

	if (tasks.length === 1) {
		const share = tasks[0]!;
		const task = share.task;
		const canUnlink = share.via !== 'source' && !share.logged;
		return (
			<>
				<div className={styles.row}>
					<span className={styles.key}>{label}</span>
					<span className={styles.taskValue}>
						<button
							type="button"
							className={styles.value}
							onClick={() => void openTask(task)}
							title={task.name}
							data-flx="konvo-agents.time.task"
						>
							{task.status ? <TaskStatusRing task={task} /> : <ClickUpLogo size={16} />}
							<span className={styles.taskName}>{task.name}</span>
						</button>
						{/* Phones keep the room for the name; the task view opens it in ClickUp there. */}
						{task.status && task.url && !phone && (
							<button
								type="button"
								className={styles.iconButton}
								onClick={() => void openExternalUrl(task.url!)}
								aria-label={OPEN_IN_CLICKUP_LABEL(i18n)}
								title={OPEN_IN_CLICKUP_LABEL(i18n)}
								data-flx="konvo-agents.time.task-clickup"
							>
								<ClickUpLogo size={15} />
							</button>
						)}
						{canUnlink && (
							<button
								type="button"
								className={styles.iconButton}
								onClick={() =>
									void run('unlink', () => AgentTime.changeTasks(machineId, threadId, {op: 'unlink', taskId: task.id}))
								}
								aria-label={t({
									message: 'Unlink the task',
									comment: 'Agents Time and client: remove the ClickUp task the person linked to this chat.',
								})}
								title={t({
									message: 'Unlink the task',
									comment: 'Agents Time and client: remove the ClickUp task the person linked to this chat.',
								})}
								data-flx="konvo-agents.time.task-unlink"
							>
								<IconLinkOff size={15} stroke={1.7} />
							</button>
						)}
						<button
							type="button"
							className={clsx(styles.iconButton, !phone && !picking && styles.addIcon)}
							onClick={() => setPicking(!picking)}
							aria-expanded={picking}
							aria-label={ADD_TASK_LABEL(i18n)}
							title={ADD_TASK_LABEL(i18n)}
							data-flx="konvo-agents.time.task-add"
						>
							<IconPlus size={15} stroke={1.7} />
						</button>
					</span>
				</div>
				{share.via === 'auto' && share.why && (
					<LinkedWhy
						why={share.why}
						phone={phone}
						onNotThis={() =>
							void run('unlink', () => AgentTime.changeTasks(machineId, threadId, {op: 'unlink', taskId: task.id}))
						}
					/>
				)}
				{picker}
			</>
		);
	}

	return (
		<div className={styles.tasks} data-flx="konvo-agents.time.tasks">
			<div className={styles.tasksHead}>
				<span className={styles.key}>
					{t({
						message: 'ClickUp tasks',
						comment: 'Agents Time and client: heading over the chat’s several ClickUp tasks.',
					})}
				</span>
				<span className={styles.from}>{i18n._(TASK_COUNT, {count: tasks.length})}</span>
			</div>
			{tasks.map((share, index) => (
				<TaskShareRow key={share.task.id} share={share} index={index} />
			))}
			<TurnStrip view={view} tasks={tasks} />
			<div className={clsx(styles.by, styles.byFlat)} data-flx="konvo-agents.time.split-by">
				<IconSparkles size={15} stroke={1.7} className={styles.byIcon} />
				<span>
					{t({
						message: 'Konvo split the turns by what you asked in each.',
						comment: 'Agents Time and client: each turn’s time goes to the task its prompt was about.',
					})}{' '}
					<button
						type="button"
						className={styles.linkButton}
						onClick={onTurns}
						data-flx="konvo-agents.time.check-turns"
					>
						{t({
							message: 'Check the turns',
							comment: 'Agents Time and client: opens the Turns view (each turn and its task).',
						})}
					</button>
				</span>
			</div>
			<button
				type="button"
				className={styles.addTask}
				onClick={() => setPicking(!picking)}
				aria-expanded={picking}
				data-flx="konvo-agents.time.task-add"
			>
				<IconPlus size={16} stroke={1.7} />
				{ADD_TASK_LABEL(i18n)}
			</button>
			{picker}
		</div>
	);
});

const ADD_TASK = msg({
	message: 'Add a task',
	comment: 'Agents Time and client: link another ClickUp task to this chat.',
});
const TASK_COUNT = msg({
	message: '{count, plural, one {# task} other {# tasks}}',
	comment: 'Agents Time and client: how many ClickUp tasks the chat has, e.g. "2 tasks".',
});

/** One of several tasks: its colour, name, turns and status, agent time and the person's minutes. */
function TaskShareRow({share, index}: {share: AgentTimeTaskShare; index: number}) {
	const {i18n, t} = useLingui();
	const you = minutesAmount(i18n, share.youMinutes);
	const detail = [turnsLabel(i18n, share.turns), share.task.status ? capitalise(share.task.status.name) : null]
		.filter(Boolean)
		.join(' · ');
	return (
		<div className={styles.taskRow} data-flx="konvo-agents.time.task-row">
			<i className={styles.swatch} style={{background: taskColor(index)}} aria-hidden="true" />
			<button
				type="button"
				className={styles.taskText}
				onClick={() => void openTask(share.task)}
				title={share.task.name}
			>
				<b>{share.task.name}</b>
				<span>{detail}</span>
			</button>
			<span className={styles.taskTime}>
				{timeAmount(i18n, share.workedMs)}
				<span>
					{t({
						message: `you ~${you}`,
						comment: 'Agents Time and client: the person’s own minutes on one of the chat’s tasks, e.g. "you ~13 min".',
					})}
				</span>
			</span>
		</div>
	);
}
const ADD_TASK_LABEL = (i18n: I18n) => i18n._(ADD_TASK);

/** An older API's single task as a share of the whole chat. */
export function legacyShare(view: AgentTimeView): AgentTimeTaskShare {
	return {
		task: view.task!,
		via: view.taskVia === 'source' ? 'source' : 'picked',
		why: null,
		turns: (view.turnTasks ?? []).map((turn) => turn.n),
		workedMs: view.workedMs,
		youMinutes: view.you.minutes,
		unloggedMs: view.unloggedMs,
		youUnloggedMinutes: view.you.unloggedMinutes,
		logged: view.logged,
	};
}

/** A block per turn, as wide as its time, in its task's colour; start and end under it. */
function TurnStrip({view, tasks}: {view: AgentTimeView; tasks: ReadonlyArray<AgentTimeTaskShare>}) {
	const {i18n, t} = useLingui();
	const turns = (view.turnTasks ?? []).filter((turn) => turn.ms > 0);
	if (turns.length === 0) return null;
	const index = new Map(tasks.map((share, i) => [share.task.id, i]));
	const clock = (iso: string | null) =>
		iso ? new Intl.DateTimeFormat(i18n.locale, {hour: '2-digit', minute: '2-digit'}).format(new Date(iso)) : '';
	const last = turns.at(-1)!;
	const end = last.at ? new Date(Date.parse(last.at) + last.ms).toISOString() : null;
	return (
		<div className={styles.stripArea} data-flx="konvo-agents.time.strip">
			<div className={styles.strip}>
				{turns.map((turn) => (
					<i
						key={turn.id}
						style={{flexGrow: Math.max(1, turn.ms), background: taskColor(index.get(turn.taskId ?? '') ?? 0)}}
						title={turnsLabel(i18n, [turn.n])}
					/>
				))}
			</div>
			<div className={styles.stripLabels}>
				<span>{clock(turns[0]!.at)}</span>
				<span>
					{t({
						message: 'each block is a turn',
						comment: 'Agents Time and client: under the strip of turns, one block per turn.',
					})}
				</span>
				<span>{clock(end)}</span>
			</div>
		</div>
	);
}

// ── Konvo suggests ────────────────────────────────────────────────────

function Suggestions({
	options,
	phone,
	busy,
	onLink,
	onFind,
	onNone,
}: {
	options: ReadonlyArray<AgentTaskSuggestion>;
	phone: boolean;
	busy: string | null;
	onLink: (taskId: string) => void;
	onFind: () => void;
	onNone: () => void;
}) {
	const {t} = useLingui();
	return (
		<div className={styles.suggest} data-flx="konvo-agents.time.suggest">
			<div className={styles.suggestHead}>
				<IconSparkles size={16} stroke={1.7} />
				{t({
					message: 'Konvo suggests',
					comment: 'Agents Time and client: heading over the ClickUp tasks Konvo thinks the chat is for.',
				})}
				{!phone && (
					<span>
						{t({
							message: 'from this chat',
							comment: 'Agents Time and client: Konvo’s suggestions come from what the chat says.',
						})}
					</span>
				)}
			</div>
			{options.map((option) => (
				<SuggestionCard
					key={option.task.id}
					option={option}
					phone={phone}
					busy={busy === `link:${option.task.id}`}
					onLink={() => onLink(option.task.id)}
				/>
			))}
			<div className={styles.suggestFoot}>
				<button type="button" className={styles.findButton} onClick={onFind} data-flx="konvo-agents.time.find-task">
					<IconSearch size={15} stroke={1.8} />
					{t({
						message: 'Find another task',
						comment: 'Agents Time and client: opens the task picker instead of Konvo’s suggestions.',
					})}
				</button>
				{!phone && (
					<button
						type="button"
						className={styles.noneButton}
						onClick={onNone}
						disabled={busy === 'none'}
						data-flx="konvo-agents.time.none"
					>
						{t({message: 'None of these', comment: 'Agents Time and client: hide Konvo’s suggestions for this chat.'})}
					</button>
				)}
			</div>
		</div>
	);
}

/** One suggested task: its name, Konvo's reason, status and list, and Link. */
export function SuggestionCard({
	option,
	phone,
	busy,
	onLink,
	tone = 'panel',
}: {
	option: AgentTaskSuggestion;
	phone: boolean;
	busy: boolean;
	onLink: () => void;
	tone?: 'panel' | 'corner';
}) {
	const {t} = useLingui();
	const meta = taskMeta(option.task, phone);
	return (
		<div
			className={clsx(styles.option2, option.best && styles.option2Best, tone === 'corner' && styles.option2Corner)}
			data-flx="konvo-agents.time.suggestion"
			data-best={option.best ? 'true' : undefined}
		>
			<ClickUpLogo size={16} />
			<div className={styles.option2Text}>
				<b>{option.task.name}</b>
				{option.reason && <span className={styles.option2Why}>{option.reason}</span>}
				{meta && <span className={styles.option2Meta}>{meta}</span>}
			</div>
			<button
				type="button"
				className={clsx(styles.linkPill, option.best && styles.linkPillInk)}
				onClick={onLink}
				disabled={busy}
				data-flx="konvo-agents.time.suggestion-link"
			>
				{busy && <RunningRing size={12} />}
				{t({message: 'Link', comment: 'Agents Time and client: link this suggested ClickUp task to the chat.'})}
			</button>
		</div>
	);
}
